import { motion } from "framer-motion";
import { ArrowUpRight, ImagePlus, Clock } from "lucide-react";
import { EASE } from "./Reveal";

// Obvious, professional placeholder shown where a real product photograph
// will be uploaded later. Replace `product.image` in data/products.js.
export function ImagePlaceholder({ name, large = false }) {
  return (
    <div className="relative flex h-full w-full flex-col items-center justify-center gap-3 overflow-hidden bg-slate-100 p-6 text-center">
      <div className="absolute inset-0 blueprint-grid opacity-70" aria-hidden="true" />
      <span className="absolute left-3 top-3 h-4 w-4 border-l-2 border-t-2 border-brand-emeraldLight/50" aria-hidden="true" />
      <span className="absolute right-3 top-3 h-4 w-4 border-r-2 border-t-2 border-brand-emeraldLight/50" aria-hidden="true" />
      <span className="absolute bottom-3 left-3 h-4 w-4 border-b-2 border-l-2 border-brand-emeraldLight/50" aria-hidden="true" />
      <span className="absolute bottom-3 right-3 h-4 w-4 border-b-2 border-r-2 border-brand-emeraldLight/50" aria-hidden="true" />
      <span className={`relative flex items-center justify-center rounded-full border border-dashed border-brand-emeraldLight/50 bg-white/80 ${large ? "h-20 w-20" : "h-14 w-14"}`}>
        <ImagePlus className={`${large ? "h-9 w-9" : "h-6 w-6"} text-brand-emeraldDark`} />
      </span>
      <span className="relative font-mono text-[10px] uppercase tracking-[0.28em] text-brand-emeraldDark">
        Image Placeholder
      </span>
      <span className="relative max-w-[80%] truncate text-xs text-slate-500">{name} — photograph to be added</span>
    </div>
  );
}

// Premium rectangular COMING SOON banner.
export function ComingSoonBanner({ large = false }) {
  return (
    <div className="relative flex h-full w-full items-center justify-center overflow-hidden bg-slate-100">
      <div className="absolute inset-0 hazard-band opacity-60" aria-hidden="true" />
      <div className="absolute inset-0 bg-gradient-to-br from-white/90 via-slate-100/70 to-white/90" aria-hidden="true" />
      <div className="absolute inset-4 rounded-xl border border-navy-900/10" aria-hidden="true" />
      <div className="relative px-6 text-center">
        <Clock className="mx-auto mb-3 h-5 w-5 text-brand-blueDark" />
        <p className={`coming-soon-shimmer font-display font-bold uppercase tracking-[0.18em] ${large ? "text-4xl md:text-5xl" : "text-2xl"}`}>
          Coming Soon
        </p>
        <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500">
          Details will be published soon
        </p>
      </div>
    </div>
  );
}

export function ProductMedia({ product, large = false, zoom = false }) {
  if (product.comingSoon) return <ComingSoonBanner large={large} />;
  if (product.image) {
    return (
      <img
        src={product.image}
        alt={product.name}
        loading="lazy"
        className={`h-full w-full object-cover ${zoom ? "transition-transform duration-700 group-hover:scale-[1.06]" : ""}`}
      />
    );
  }
  return <ImagePlaceholder name={product.name} large={large} />;
}

// Interactive product card: hover/tap illuminates the border, zooms the media,
// shifts the title and reveals "View Details". Tap opens the modal on mobile.
export default function ProductCard({ product, index = 0, onOpen }) {
  return (
    <motion.article
      layout
      initial={{ opacity: 0, y: 26 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.96 }}
      transition={{ duration: 0.5, delay: Math.min(index * 0.05, 0.3), ease: EASE }}
      className="group relative flex h-full cursor-pointer flex-col overflow-hidden rounded-2xl border border-navy-900/10 bg-white/70 backdrop-blur-md transition-[border-color,box-shadow,transform] duration-500 hover:-translate-y-1.5 hover:border-brand-emeraldLight/50 hover:shadow-[0_24px_70px_rgba(16,185,129,0.16)]"
      onClick={() => onOpen(product)}
      data-testid="product-card"
      data-product-name={product.name}
    >
      <div className="relative aspect-[4/3] overflow-hidden border-b border-navy-900/[0.08]">
        <ProductMedia product={product} zoom />
      </div>

      <div className="flex flex-1 flex-col p-5">
        <span className="font-mono text-[10px] uppercase tracking-[0.24em] text-brand-blueDark">
          {product.comingSoon ? "Upcoming" : product.category}
        </span>
        <h3 className="mt-2 font-display text-xl font-bold uppercase leading-tight tracking-wide text-ink transition-transform duration-500 group-hover:translate-x-1">
          {product.name}
        </h3>
        <p className="mt-2 line-clamp-2 flex-1 text-sm leading-relaxed text-slate-500">{product.description}</p>

        <span className="mt-4 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-brand-emeraldDark opacity-0 transition-all duration-500 group-hover:opacity-100 sm:opacity-40 sm:group-hover:opacity-100">
          View Details
          <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </span>
      </div>

      <span
        aria-hidden="true"
        className="absolute bottom-0 left-0 h-[2px] w-0 bg-gradient-to-r from-brand-emerald to-brand-blueGlow transition-all duration-500 group-hover:w-full"
      />
    </motion.article>
  );
}
